//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import React from 'react';
import styled from 'styled-components';

import { PrimaryRouteAnchor } from '@components/RouteAnchor';
import Icon from '@components/atoms/icon/Icon';
import { BodyBoldText, BodyText } from '@components/atoms/text/Text';
import ReelId from '@models/ReelId';
import { Mixins } from '@theme/mixins';
import { UIATrackingIds } from '@uia/constants/uiaConstants';
import { isDefined } from '@utils/filterPredicates';
import { withTracking } from '@utils/withTracking';

export interface EditorDisabledNotesProps {
  hasValidProductItems: boolean;
  isProductTaggingSupported: boolean;
  numProductsWithMissingInfo: number;
  onSearchProductCatalog?: () => void;
  reelId: ReelId | undefined;
}

export const EditorDisabledNotes = ({
  hasValidProductItems,
  isProductTaggingSupported,
  numProductsWithMissingInfo,
  onSearchProductCatalog,
  reelId,
}: EditorDisabledNotesProps) => {
  return (
    <>
      {isProductTaggingSupported && (
        <BodyText>
          <Icon name='info' />
          &nbsp; To edit tagged products for this media, use&nbsp;
          {isDefined(reelId) ? (
            <>
              <PrimaryRouteAnchor
                state='galleries'
                stateParams={{ reelId }}
                trackingId={UIATrackingIds.PRODUCTS_REEL_MODERATION}
              >
                gallery moderation.
              </PrimaryRouteAnchor>
            </>
          ) : (
            'gallery moderation.'
          )}
          &nbsp;If you redistribute this media to Scheduler, another Gallery,
          etc., your product tags will not be replicated to the new item.
          Contact your Bazaarvoice Success Manager for more information.
        </BodyText>
      )}
      {hasValidProductItems && (
        <div>
          <BodyBoldText>
            There&nbsp;
            {numProductsWithMissingInfo === 1
              ? 'is 1 product'
              : `are ${numProductsWithMissingInfo} products`}
            &nbsp; with errors
          </BodyBoldText>
          <BodyText>
            Please ensure that all products have a name and media.
          </BodyText>
        </div>
      )}
      {!hasValidProductItems && !isProductTaggingSupported && (
        <BodyText>
          Click on the media to tag a product, or&nbsp;
          <InlinePrimaryText
            onClick={onSearchProductCatalog}
            trackingId={UIATrackingIds.PRODUCTS_SEARCH_PRODUCT_CATALOG}
          >
            search your product catalog.
          </InlinePrimaryText>
        </BodyText>
      )}
    </>
  );
};

const InlinePrimaryText = withTracking(styled.span`
  ${Mixins.Text.LinkPrimary}
`);
